import type { RefObject } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

/**
 * Hook for managing search dropdown behavior
 * Consolidates duplicate dropdown logic from MemberSearch, ModuleSearch, etc.
 */

export interface DropdownPosition {
  top: number
  left: number
  width: number
  maxHeight: number
  openDirection: 'down' | 'up'
}

export interface UseSearchDropdownOptions {
  /** Data attribute to identify the dropdown element */
  dropdownDataAttribute?: string
  /** Minimum search length before showing results */
  minSearchLength?: number
  /** Debounce delay in milliseconds */
  debounceDelay?: number
  /** Close dropdown on scroll */
  closeOnScroll?: boolean
}

export interface UseSearchDropdownReturn<T> {
  // State
  search: string
  setSearch: (value: string) => void
  showResults: boolean
  setShowResults: (show: boolean) => void
  isLoading: boolean
  setIsLoading: (loading: boolean) => void
  searchResults: T[]
  setSearchResults: (results: T[]) => void
  dropdownPosition: DropdownPosition

  // Refs
  inputRef: RefObject<HTMLInputElement>

  // Handlers
  updateDropdownPosition: () => void
  handleFocus: () => void
  handleBlur: () => void
  clearSearch: () => void
}

const DEFAULT_OPTIONS: UseSearchDropdownOptions = {
  dropdownDataAttribute: 'data-search-dropdown',
  minSearchLength: 2,
  debounceDelay: 300,
  closeOnScroll: true,
}

/**
 * Hook for managing search dropdown state and behavior
 *
 * @param options - Configuration options
 * @returns Search dropdown state and handlers
 *
 * @example
 * const {
 *   search,
 *   setSearch,
 *   showResults,
 *   searchResults,
 *   setSearchResults,
 *   inputRef,
 *   updateDropdownPosition,
 *   dropdownPosition
 * } = useSearchDropdown<MemberResult>({
 *   dropdownDataAttribute: 'data-member-search-dropdown',
 *   minSearchLength: 2
 * });
 */
export function useSearchDropdown<T>(
  options: UseSearchDropdownOptions = {},
): UseSearchDropdownReturn<T> {
  const {
    dropdownDataAttribute,
    minSearchLength,
    debounceDelay,
    closeOnScroll,
  } = options

  // Memoize merged options to prevent unnecessary re-renders
  const opts = useMemo(
    () => ({
      ...DEFAULT_OPTIONS,
      dropdownDataAttribute,
      minSearchLength,
      debounceDelay,
      closeOnScroll,
    }),
    [dropdownDataAttribute, minSearchLength, debounceDelay, closeOnScroll],
  )

  // State
  const [search, setSearch] = useState('')
  const [showResults, setShowResults] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [searchResults, setSearchResults] = useState<T[]>([])
  const [dropdownPosition, setDropdownPosition] = useState<DropdownPosition>({
    top: 0,
    left: 0,
    width: 0,
    maxHeight: 320,
    openDirection: 'down',
  })

  // Refs
  const inputRef = useRef<HTMLInputElement>(null)

  // Update dropdown position based on input element
  const updateDropdownPosition = useCallback(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect()
      const gap = 4
      const viewportMargin = 16
      const preferredHeight = 320
      const spaceBelow = window.innerHeight - rect.bottom - viewportMargin - gap
      const spaceAbove = rect.top - viewportMargin - gap
      const openDirection =
        spaceBelow < preferredHeight && spaceAbove > spaceBelow ? 'up' : 'down'
      const maxHeight = Math.max(
        160,
        Math.min(
          preferredHeight,
          openDirection === 'up' ? spaceAbove : spaceBelow,
        ),
      )

      setDropdownPosition({
        top:
          openDirection === 'up'
            ? rect.top - maxHeight - gap
            : rect.bottom + gap,
        left: rect.left,
        width: rect.width,
        maxHeight,
        openDirection,
      })
    }
  }, [])

  // Handle focus
  const handleFocus = useCallback(() => {
    updateDropdownPosition()
    if (
      search.length >= (opts.minSearchLength ?? 2) &&
      searchResults.length > 0
    ) {
      setShowResults(true)
    }
  }, [
    search.length,
    searchResults.length,
    opts.minSearchLength,
    updateDropdownPosition,
  ])

  // Handle blur - with delay to allow click on results
  const handleBlur = useCallback(() => {
    // Delay to allow click events on dropdown items
    setTimeout(() => {
      if (!document.activeElement?.closest(`[${opts.dropdownDataAttribute}]`)) {
        setShowResults(false)
      }
    }, 150)
  }, [opts.dropdownDataAttribute])

  // Clear search
  const clearSearch = useCallback(() => {
    setSearch('')
    setSearchResults([])
    setShowResults(false)
    setIsLoading(false)
  }, [])

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node

      // Check if click is inside the input
      if (inputRef.current?.contains(target)) {
        return
      }

      // Check if click is inside the dropdown
      const dropdownElement = document.querySelector(
        `[${opts.dropdownDataAttribute}]`,
      )
      if (dropdownElement?.contains(target)) {
        return
      }

      setShowResults(false)
    }

    if (showResults) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showResults, opts.dropdownDataAttribute])

  // Close on scroll (optional)
  useEffect(() => {
    if (!opts.closeOnScroll) return

    const handleScroll = (event: Event) => {
      const target = event.target as Element
      const dropdownElement = document.querySelector(
        `[${opts.dropdownDataAttribute}]`,
      )

      // Don't close if scrolling inside the dropdown
      if (dropdownElement?.contains(target)) {
        return
      }

      setShowResults(false)
    }

    if (showResults) {
      window.addEventListener('scroll', handleScroll, true)
    }

    return () => {
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [showResults, opts.closeOnScroll, opts.dropdownDataAttribute])

  // Update position on resize
  useEffect(() => {
    const handleResize = () => {
      if (showResults) {
        updateDropdownPosition()
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [showResults, updateDropdownPosition])

  return {
    // State
    search,
    setSearch,
    showResults,
    setShowResults,
    isLoading,
    setIsLoading,
    searchResults,
    setSearchResults,
    dropdownPosition,

    // Refs
    inputRef: inputRef as RefObject<HTMLInputElement>,

    // Handlers
    updateDropdownPosition,
    handleFocus,
    handleBlur,
    clearSearch,
  }
}

/**
 * Debounce utility for search functions
 */
export function useDebounceValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(timer)
    }
  }, [value, delay])

  return debouncedValue
}
